import { useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { ColorField } from "@/components/cms/theme/ColorField";
import { BackgroundImageField } from "@/components/cms-puck/fields/BackgroundImageField";
import { ContainerHeightControls } from "@/components/cms-puck/fields/ContainerHeightField";
import { CmsTypographyPicker } from "@/components/cms/theme/CmsTypographyPicker";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import { type CmsColor } from "@/lib/cms/cms-theme.constants";
import { useState } from "react";
import {
  normalizeBlockStyle,
  combineEntrance,
  entranceDirection,
  entranceKind,
  type BlockEffects,
  type BlockEntranceDirection,
  type BlockEntranceKind,
  type BlockLayoutAlign,
  type BlockLayoutStyle,
  type BlockShadow,
  type BlockSpacingBox,
  type BlockStyle,
  type BlockTextStyle,
  type ResponsiveStylePatch,
  type SpacingSide,
  type SpacingUnit,
  type StyleDevice,
} from "@/lib/cms-puck/block-style";
import {
  getLayoutMaxWidthOptions,
  type LayoutMaxWidth,
} from "@/lib/cms-puck/layout";
import { Switch } from "@/components/ui/switch";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Monitor,
  Pin,
  ArrowUpFromLine,
  ArrowDownFromLine,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  MoveHorizontal,
  Smartphone,
  Tablet,
} from "lucide-react";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import { cn } from "@/lib/utils";
import type { ContainerHeight } from "@/lib/cms-puck/layout";
import {
  FLOW_BLOCKS,
  NO_BACKGROUND_IMAGE_BLOCKS,
  NO_LAYOUT_BLOCKS,
  TEXT_TREATMENT_BLOCKS,
} from "./style/constants";
import { StyleSection } from "./style/StyleSection";
import { ControlRow, IconToggle } from "./style/controls";
import { SPACING_UNITS, SpacingBoxEditor } from "./style/SpacingBoxEditor";
import { BorderControls } from "./style/BorderControls";
import { RadiusControls } from "./style/RadiusControls";
import {
  DeviceSwitcher,
  inheritedBox,
  inheritedScalar,
} from "./style/DeviceSwitcher";
import { TextControls } from "./style/TextControls";
import { FlowControls } from "./style/FlowControls";

/**
 * Contrôles « Style » d'un bloc, scindés en deux groupes RÉUTILISABLES rendus par
 * le panneau à 3 onglets (Contenu | Design | Avancé) via l'override Puck
 * `overrides.fields` (cf. `cms-puck/fields/FieldsPanel.tsx`) :
 *  - `DesignControls`  → onglet « Design »  (fond, couleur texte, typographie) ;
 *  - `SpacingControls` → onglet « Avancé »  (margin/padding par côté + unité).
 *
 * Tous lisent/écrivent un `BlockStyle` complet (forme de données INCHANGÉE) et
 * lisent la palette + défauts typo depuis `usePuckTheme()` (Provider monté autour
 * de `<Puck>`). onChange immuable. La couleur libre N'EST PAS ajoutée à la palette
 * (ColorField non-polluant).
 */

const SHADOW_LABELS: Array<[BlockShadow, CmsNewKey, string]> = [
  ["none", "cms.new.auto.option.none.aucune", "Aucune"],
  ["sm", "cms.new.auto.option.sm.legere", "Légère"],
  ["md", "cms.new.auto.option.md.moyenne", "Moyenne"],
  ["lg", "cms.new.auto.option.lg.grande", "Grande"],
  ["xl", "cms.new.auto.option.xl.tres_grande", "Très grande"],
];

/**
 * Style de TEXTE réutilisable : Typographie (police/graisse) + Traitement du
 * texte (taille, alignement, casse, décoration…). Extrait de `DesignControls`
 * pour pouvoir être rendu AILLEURS que dans l'onglet Design — notamment dans
 * l'onglet « Contenu » pour les blocs de texte (cohérence : le style du texte
 * est édité au même endroit que le contenu). `DesignControls` peut alors le
 * masquer via `hideTextStyle` pour éviter le doublon.
 */
export function TextStyleControls({
  value,
  onChange,
  blockType,
}: {
  value: BlockStyle | undefined;
  onChange: (value: BlockStyle) => void;
  blockType?: string;
}) {
  const cmsNew = useCmsNew();
  const theme = usePuckTheme();
  const style = normalizeBlockStyle(value);

  const updateDesign = (patch: Partial<BlockStyle["design"]>) =>
    onChange({ ...style, design: { ...style.design, ...patch } });
  const updateText = (patch: Partial<BlockTextStyle>) => {
    const text = { ...style.design.text, ...patch };
    // Sans alignement Bureau, les surcharges d'alignement ne s'appliquent pas
    // (cf. textAlignHasResponsive) : on les efface plutôt que de les laisser
    // dormir, invisibles, jusqu'au prochain alignement Bureau.
    const responsive =
      "align" in patch && text.align == null
        ? {
            ...style.responsive,
            tablet: { ...style.responsive.tablet, textAlign: null },
            mobile: { ...style.responsive.mobile, textAlign: null },
          }
        : style.responsive;
    onChange({ ...style, design: { ...style.design, text }, responsive });
  };
  const updateResponsive = (
    device: "tablet" | "mobile",
    patch: Partial<ResponsiveStylePatch>,
  ) =>
    onChange({
      ...style,
      responsive: {
        ...style.responsive,
        [device]: { ...style.responsive[device], ...patch },
      },
    });

  const showTextTreatment = blockType != null && TEXT_TREATMENT_BLOCKS.has(blockType);

  return (
    <>
      <StyleSection
        title={cmsNew("cms.new.auto.title.typographie", "Typographie")}
        description={cmsNew("cms.new.auto.description.police_et_graisse_appliquees_au_contenu_du_bloc", "Police et graisse appliquées au contenu du bloc.")}
      >
        <CmsTypographyPicker
          label={cmsNew("cms.new.auto.label.style_de_texte", "Style de texte")}
          allowNone
          hidePreview
          value={style.design.typography}
          inheritDisplayValue={{
            fontFamily: theme?.defaultPageFontFamily ?? null,
            fontVariant: theme?.defaultPageFontVariant ?? null,
          }}
          onChange={(typography) => updateDesign({ typography })}
        />
      </StyleSection>

      {showTextTreatment ? (
        <TextControls
          style={style}
          onChange={updateText}
          onResponsiveChange={updateResponsive}
          // RichText : gras/italique/souligné/barré sont INLINE (éditeur), pas
          // au niveau bloc → on masque la rangée « Style » ici.
          showDecoration={blockType !== "RichText"}
          blockType={blockType}
        />
      ) : null}
    </>
  );
}

/**
 * Onglet « Design » : fond + couleur du texte (via ColorField amélioré) +
 * typographie (via CmsTypographyPicker) + traitement de texte (Titre/Texte riche).
 * Écrit `style.design`.
 *
 * `hideTextStyle` : masque Typographie + Traitement du texte (rendus ailleurs,
 * p. ex. dans l'onglet Contenu pour les blocs de texte, cf. `TextStyleControls`).
 */
export function DesignControls({
  value,
  onChange,
  blockType,
  hideTextStyle = false,
}: {
  value: BlockStyle | undefined;
  onChange: (value: BlockStyle) => void;
  blockType?: string;
  hideTextStyle?: boolean;
}) {
  const cmsNew = useCmsNew();
  const theme = usePuckTheme();
  const colors: CmsColor[] = theme?.colors ?? [];
  const style = normalizeBlockStyle(value);

  const updateDesign = (patch: Partial<BlockStyle["design"]>) =>
    onChange({ ...style, design: { ...style.design, ...patch } });

  const updateBackgroundImage = (backgroundImage: BlockStyle["design"]["backgroundImage"]) =>
    updateDesign({ backgroundImage });

  const showBackgroundImage = !NO_BACKGROUND_IMAGE_BLOCKS.has(blockType ?? "");

  return (
    <div className="space-y-4">
      <StyleSection
        title={cmsNew("cms.new.auto.title.couleurs", "Couleurs")}
        description={cmsNew("cms.new.auto.description.fond_et_couleur_du_texte_herite_reprend_les_valeurs_du_theme", "Fond et couleur du texte. « Hérite » reprend les valeurs du thème.")}
      >
        <div className="space-y-4">
          <ColorField
            label={cmsNew("cms.new.auto.label.fond", "Fond")}
            colors={colors}
            value={style.design.background}
            onChange={(background) => updateDesign({ background })}
          />
          <ColorField
            label={cmsNew("cms.new.auto.label.couleur_du_texte", "Couleur du texte")}
            colors={colors}
            value={style.design.textColor}
            onChange={(textColor) => updateDesign({ textColor })}
          />
        </div>
      </StyleSection>

      {showBackgroundImage ? (
        <StyleSection
          title={cmsNew("cms.new.auto.title.media_de_fond", "Média de fond")}
          description={cmsNew("cms.new.auto.description.image_en_arriere_plan_de_la_section_avec_ajustement_et_degrade_optionnel", "Image en arrière-plan de la section, avec ajustement et dégradé optionnel.")}
        >
          <BackgroundImageField
            value={style.design.backgroundImage}
            onChange={updateBackgroundImage}
            colors={colors}
          />
        </StyleSection>
      ) : null}

      {hideTextStyle ? null : (
        <TextStyleControls value={value} onChange={onChange} blockType={blockType} />
      )}

      <BorderControls
        border={style.design.border}
        colors={colors}
        onBorderChange={(border) => updateDesign({ border })}
      />

      <RadiusControls
        radius={style.design.radius}
        onRadiusChange={(radius) => updateDesign({ radius })}
      />

      <StyleSection
        title={cmsNew("cms.new.auto.title.ombre", "Ombre")}
        description={cmsNew("cms.new.auto.description.ombre_portee_du_bloc_box_shadow", "Ombre portée du bloc (box-shadow).")}
      >
        <ControlRow label={cmsNew("cms.new.auto.label.intensite", "Intensité")}>
          <Select
            value={style.design.shadow}
            onValueChange={(shadow) =>
              updateDesign({ shadow: shadow as BlockShadow })
            }
          >
            <SelectTrigger className="h-9 w-36 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SHADOW_LABELS.map(([value, key, fallback]) => (
                <SelectItem key={value} value={value}>
                  {cmsNew(key, fallback)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ControlRow>
      </StyleSection>
    </div>
  );
}

const LAYOUT_ALIGN_OPTIONS: Array<
  [BlockLayoutAlign, CmsNewKey, string, React.ReactNode]
> = [
  ["left", "cms.new.fields.style.blockAlignLeft", "À gauche", <AlignLeft className="h-4 w-4" />],
  ["center", "cms.new.fields.style.blockAlignCenter", "Centré", <AlignCenter className="h-4 w-4" />],
  ["right", "cms.new.fields.style.blockAlignRight", "À droite", <AlignRight className="h-4 w-4" />],
];

const VISIBILITY_ROWS: Array<
  [keyof BlockStyle["visibility"], CmsNewKey, string, React.ReactNode]
> = [
  ["hideDesktop", "cms.new.fields.style.hideDesktop", "Masquer sur bureau", <Monitor className="h-4 w-4" />],
  ["hideTablet", "cms.new.fields.style.hideTablet", "Masquer sur tablette", <Tablet className="h-4 w-4" />],
  ["hideMobile", "cms.new.fields.style.hideMobile", "Masquer sur mobile", <Smartphone className="h-4 w-4" />],
];

/**
 * Onglet « Avancé » façon Elementor :
 *  - bascule Bureau/Tablette/Mobile (les vues tablette/mobile n'éditent QUE les
 *    valeurs dimensionnelles, en surcharge héritée du niveau supérieur) ;
 *  - Espacement : margin/padding par côté + unité commune (`style.spacing` +
 *    `style.responsive.*`) ;
 *  - Mise en page : largeur (responsive), largeur max, alignement du bloc,
 *    z-index (`style.layout`) ;
 *  - Visibilité par appareil (`style.visibility`) ;
 *  - Attributs : ID + classes CSS custom (`style.attributes`).
 *
 * Blocs sans wrapper (Bouton/Icône) : depuis l'unification ils portent eux-mêmes
 * les règles générées → tout est disponible SAUF la « Mise en page »
 * (largeur/alignement/z-index, cf. NO_LAYOUT_BLOCKS).
 */
export function SpacingControls({
  value,
  onChange,
  blockType,
  containerHeight,
  onContainerHeightChange,
  containerContentMaxWidth,
  blockProps,
}: {
  value: BlockStyle | undefined;
  onChange: (value: BlockStyle) => void;
  blockType?: string;
  /** Hauteur structurelle du Conteneur (prop `height`, pas `style.layout`). */
  containerHeight?: ContainerHeight;
  onContainerHeightChange?: (value: ContainerHeight) => void;
  /**
   * Repli d'affichage pour le Conteneur : `props.maxWidth` (onglet Contenu) quand
   * `style.layout.contentMaxWidth` n'est pas défini.
   */
  containerContentMaxWidth?: LayoutMaxWidth;
  /** Props du bloc : valeurs Bureau de la disposition (Container / Columns / Grid). */
  blockProps?: Record<string, unknown>;
}) {
  const cmsNew = useCmsNew();
  const style = normalizeBlockStyle(value);
  const showLayout = !NO_LAYOUT_BLOCKS.has(blockType ?? "");
  const [device, setDevice] = useState<StyleDevice>("desktop");
  const contentMaxWidthDisplay =
    style.layout.contentMaxWidth ?? containerContentMaxWidth ?? "full";

  const updateSpacing = (patch: Partial<BlockStyle["spacing"]>) =>
    onChange({ ...style, spacing: { ...style.spacing, ...patch } });
  const updateResponsive = (
    d: "tablet" | "mobile",
    patch: Partial<ResponsiveStylePatch>,
  ) =>
    onChange({
      ...style,
      responsive: {
        ...style.responsive,
        [d]: { ...style.responsive[d], ...patch },
      },
    });
  const updateLayout = (patch: Partial<BlockLayoutStyle>) =>
    onChange({ ...style, layout: { ...style.layout, ...patch } });
  const updateVisibility = (patch: Partial<BlockStyle["visibility"]>) =>
    onChange({ ...style, visibility: { ...style.visibility, ...patch } });
  const updateAttributes = (patch: Partial<BlockStyle["attributes"]>) =>
    onChange({ ...style, attributes: { ...style.attributes, ...patch } });
  const updateEffects = (patch: Partial<BlockEffects>) =>
    onChange({ ...style, effects: { ...style.effects, ...patch } });

  const ENTRANCE_KIND_OPTIONS: Array<
    [BlockEntranceKind, string, React.ReactNode]
  > = [
    ["none", cmsNew("cms.new.fields.style.entranceNone", "Aucune"), <span className="text-[10px] font-semibold">—</span>],
    ["fade-in", cmsNew("cms.new.fields.style.entranceFade", "Fondu"), <span className="text-[10px] font-semibold">F</span>],
    ["slide", cmsNew("cms.new.fields.style.entranceSlide", "Glissement"), <MoveHorizontal className="h-4 w-4" />],
  ];

  const ENTRANCE_DIRECTION_OPTIONS: Array<
    [BlockEntranceDirection, string, React.ReactNode]
  > = [
    ["up", cmsNew("cms.new.fields.style.entranceDirUp", "Haut"), <ArrowUpFromLine className="h-4 w-4" />],
    ["down", cmsNew("cms.new.fields.style.entranceDirDown", "Bas"), <ArrowDownFromLine className="h-4 w-4" />],
    ["left", cmsNew("cms.new.fields.style.entranceDirLeft", "Gauche"), <ArrowLeftFromLine className="h-4 w-4" />],
    ["right", cmsNew("cms.new.fields.style.entranceDirRight", "Droite"), <ArrowRightFromLine className="h-4 w-4" />],
  ];

  const currentEntranceKind = entranceKind(style.effects.entrance);
  const currentEntranceDirection = entranceDirection(style.effects.entrance);

  // Boîtes et largeur selon l'appareil courant (desktop = champs historiques).
  const onDesktop = device === "desktop";
  const marginBox = onDesktop ? style.spacing.margin : style.responsive[device].margin;
  const paddingBox = onDesktop
    ? style.spacing.padding
    : style.responsive[device].padding;
  const setMargin = (margin: BlockSpacingBox) =>
    onDesktop ? updateSpacing({ margin }) : updateResponsive(device, { margin });
  const setPadding = (padding: BlockSpacingBox) =>
    onDesktop
      ? updateSpacing({ padding })
      : updateResponsive(device, { padding });
  const widthValue = onDesktop ? style.layout.width : style.responsive[device].width;
  const setWidth = (width: SpacingSide) =>
    onDesktop ? updateLayout({ width }) : updateResponsive(device, { width });
  const autoPlaceholder = cmsNew("cms.new.auto.placeholder.auto", "Auto");
  const widthPlaceholder = onDesktop
    ? autoPlaceholder
    : String(inheritedScalar(style, device, "width") ?? autoPlaceholder);

  return (
    <div className="space-y-4">
      <DeviceSwitcher device={device} onChange={setDevice} />

      <SpacingBoxEditor
        title={cmsNew("cms.new.auto.title.marge_exterieure", "Marge extérieure")}
        description={cmsNew("cms.new.auto.description.espace_autour_du_bloc_margin_css", "Espace autour du bloc (margin CSS).")}
        box={marginBox}
        placeholders={onDesktop ? undefined : inheritedBox(style, device, "margin")}
        onChange={setMargin}
      />
      <SpacingBoxEditor
        title={cmsNew("cms.new.auto.title.marge_interieure", "Marge intérieure")}
        description={cmsNew("cms.new.auto.description.espace_a_linterieur_du_bloc_padding_css", "Espace à l'intérieur du bloc (padding CSS).")}
        box={paddingBox}
        placeholders={onDesktop ? undefined : inheritedBox(style, device, "padding")}
        onChange={setPadding}
      />

      {blockType && FLOW_BLOCKS.has(blockType) ? (
        <FlowControls
          device={device}
          style={style}
          blockType={blockType}
          blockProps={blockProps ?? {}}
          onChange={(d, patch) =>
            updateResponsive(d, {
              flow: { ...style.responsive[d].flow, ...patch },
            })
          }
        />
      ) : null}

      {onDesktop ? (
        <StyleSection title={cmsNew("cms.new.auto.title.unite", "Unité")} description={cmsNew("cms.new.auto.description.appliquee_a_toutes_les_valeurs_despacement_tous_appareils", "Appliquée à toutes les valeurs d'espacement (tous appareils).")}>
          <Select
            value={style.spacing.unit}
            onValueChange={(unit) => updateSpacing({ unit: unit as SpacingUnit })}
          >
            <SelectTrigger className="h-9 w-28 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPACING_UNITS.map((u) => (
                <SelectItem key={u} value={u}>
                  {u}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </StyleSection>
      ) : null}

      {showLayout ? (
        <StyleSection
          title={cmsNew("cms.new.auto.title.mise_en_page", "Mise en page")}
          description={cmsNew("cms.new.auto.description.largeur_alignement_et_empilement_du_bloc", "Largeur, alignement et empilement du bloc.")}
        >
          <div className="space-y-4">
            <ControlRow label={cmsNew("cms.new.auto.label.largeur", "Largeur")}>
              <input
                type="number"
                min={0}
                value={widthValue ?? ""}
                placeholder={widthPlaceholder}
                className={cn(
                  CMS_PALETTE_TEXT_INPUT_CLASS,
                  "h-9 w-24 text-center tabular-nums",
                )}
                onChange={(e) => {
                  const raw = e.target.value;
                  const n = raw === "" ? null : Number(raw);
                  setWidth(n != null && Number.isFinite(n) && n > 0 ? n : null);
                }}
              />
              <Select
                value={style.layout.widthUnit}
                onValueChange={(widthUnit) =>
                  updateLayout({
                    widthUnit: widthUnit as BlockLayoutStyle["widthUnit"],
                  })
                }
              >
                <SelectTrigger className="h-9 w-20 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="%">%</SelectItem>
                  <SelectItem value="px">{cmsNew("cms.new.auto.text.px", "px")}</SelectItem>
                </SelectContent>
              </Select>
            </ControlRow>

            {blockType === "Container" && onContainerHeightChange ? (
              <ControlRow label={cmsNew("cms.new.auto.label.hauteur", "Hauteur")}>
                <ContainerHeightControls
                  value={containerHeight}
                  onChange={onContainerHeightChange}
                />
              </ControlRow>
            ) : null}

            {onDesktop ? (
              <>
                <ControlRow label={cmsNew("cms.new.auto.label.largeur_max_du_composant_px", "Largeur max du composant (px)")}>
                  <input
                    type="number"
                    min={0}
                    value={style.layout.maxWidth ?? ""}
                    placeholder={cmsNew("cms.new.auto.placeholder.26aeab", "—")}
                    className={cn(
                      CMS_PALETTE_TEXT_INPUT_CLASS,
                      "h-9 w-24 text-center tabular-nums",
                    )}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === "" ? null : Number(raw);
                      updateLayout({
                        maxWidth:
                          n != null && Number.isFinite(n) && n > 0 ? n : null,
                      });
                    }}
                  />
                </ControlRow>

                <ControlRow label={cmsNew("cms.new.auto.label.alignement_du_bloc", "Alignement du bloc")}>
                  {LAYOUT_ALIGN_OPTIONS.map(([value, key, fallback, icon]) => (
                    <IconToggle
                      key={value}
                      label={cmsNew(key, fallback)}
                      active={style.layout.align === value}
                      onClick={() =>
                        updateLayout({
                          align: style.layout.align === value ? null : value,
                        })
                      }
                    >
                      {icon}
                    </IconToggle>
                  ))}
                </ControlRow>

                <ControlRow label={cmsNew("cms.new.auto.label.largeur_max_du_contenu", "Largeur max du contenu")}>
                  <Select
                    value={contentMaxWidthDisplay}
                    onValueChange={(v) =>
                      updateLayout({
                        contentMaxWidth: v as LayoutMaxWidth,
                      })
                    }
                  >
                    <SelectTrigger className="h-9 w-full max-w-xs bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getLayoutMaxWidthOptions().map(({ label, value }) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </ControlRow>
                {blockType === "Container" &&
                style.layout.contentMaxWidth == null ? (
                  <p className="text-xs leading-relaxed text-slate-400">
                    {cmsNew(
                      "cms.new.fields.style.contentMaxWidthInheritHint",
                      "Valeur héritée de l'onglet Contenu (« Largeur maximale ») tant qu'elle n'est pas modifiée ici.",
                    )}
                  </p>
                ) : null}

                <ControlRow label={cmsNew("cms.new.auto.label.z_index", "Z-index")}>
                  <input
                    type="number"
                    value={style.layout.zIndex ?? ""}
                    placeholder={cmsNew("cms.new.auto.placeholder.26aeab", "—")}
                    className={cn(
                      CMS_PALETTE_TEXT_INPUT_CLASS,
                      "h-9 w-24 text-center tabular-nums",
                    )}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === "" ? null : Number(raw);
                      updateLayout({
                        zIndex: n != null && Number.isFinite(n) ? n : null,
                      });
                    }}
                  />
                </ControlRow>
              </>
            ) : (
              <p className="text-xs leading-relaxed text-slate-400">
                {cmsNew(
                  "cms.new.fields.style.layoutResponsiveHint",
                  "Largeur max, alignement, largeur max du contenu et z-index se règlent en vue Bureau.",
                )}
              </p>
            )}
          </div>
        </StyleSection>
      ) : null}

      {onDesktop ? (
        <StyleSection
          title={cmsNew("cms.new.auto.title.effets", "Effets")}
          description={cmsNew("cms.new.auto.description.position_sticky_et_animation_dentree_legere_au_chargement", "Position sticky et animation d'entrée légère au chargement.")}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-xs font-medium text-slate-500">
                <Pin className="h-4 w-4" />
                {cmsNew("cms.new.fields.style.sticky", "Sticky (fixé au scroll)")}
              </span>
              <Switch
                checked={style.effects.sticky}
                onCheckedChange={(checked) =>
                  updateEffects({ sticky: checked === true })
                }
              />
            </div>
            {style.effects.sticky ? (
              <ControlRow label={cmsNew("cms.new.auto.label.offset_haut_px", "Offset haut (px)")}>
                <input
                  type="number"
                  min={0}
                  value={style.effects.stickyTop ?? 0}
                  className={cn(
                    CMS_PALETTE_TEXT_INPUT_CLASS,
                    "h-9 w-24 text-center tabular-nums",
                  )}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    updateEffects({
                      stickyTop:
                        Number.isFinite(n) && n >= 0 ? Math.round(n) : 0,
                    });
                  }}
                />
              </ControlRow>
            ) : null}
            <ControlRow label={cmsNew("cms.new.auto.label.animation_dentree", "Animation d'entrée")}>
              {ENTRANCE_KIND_OPTIONS.map(([value, label, icon]) => (
                <IconToggle
                  key={value}
                  label={label}
                  active={currentEntranceKind === value}
                  onClick={() =>
                    updateEffects({
                      entrance: combineEntrance(
                        value,
                        value === "slide" ? currentEntranceDirection : "up",
                      ),
                    })
                  }
                >
                  {icon}
                </IconToggle>
              ))}
            </ControlRow>
            {currentEntranceKind === "slide" ? (
              <ControlRow label={cmsNew("cms.new.auto.label.sens_du_glissement", "Sens du glissement")}>
                {ENTRANCE_DIRECTION_OPTIONS.map(([value, label, icon]) => (
                  <IconToggle
                    key={value}
                    label={label}
                    active={currentEntranceDirection === value}
                    onClick={() =>
                      updateEffects({
                        entrance: combineEntrance("slide", value),
                      })
                    }
                  >
                    {icon}
                  </IconToggle>
                ))}
              </ControlRow>
            ) : null}
          </div>
        </StyleSection>
      ) : null}

      <StyleSection
        title={cmsNew("cms.new.auto.title.visibilite", "Visibilité")}
        description={cmsNew("cms.new.auto.description.masque_le_bloc_sur_certains_appareils_sur_le_site_publie_et_dans_lapercu", "Masque le bloc sur certains appareils (sur le site publié ET dans l'aperçu).")}
      >
        <div className="space-y-3">
          {VISIBILITY_ROWS.map(([visibilityKey, key, fallback, icon]) => (
            <div
              key={visibilityKey}
              className="flex items-center justify-between gap-3"
            >
              <span className="flex items-center gap-2 text-xs font-medium text-slate-500">
                {icon}
                {cmsNew(key, fallback)}
              </span>
              <Switch
                checked={style.visibility[visibilityKey]}
                onCheckedChange={(checked) =>
                  updateVisibility({ [visibilityKey]: checked === true })
                }
              />
            </div>
          ))}
        </div>
      </StyleSection>

      {onDesktop ? (
        <StyleSection
          title={cmsNew("cms.new.auto.title.attributs", "Attributs")}
          description={cmsNew("cms.new.auto.description.id_et_classes_css_sur_le_conteneur_du_bloc_ciblez_les_dans_longlet_css_reglages_", "ID et classes CSS sur le conteneur du bloc — ciblez-les dans l'onglet CSS (réglages de la page).")}
        >
          <div className="space-y-4">
            <ControlRow label={cmsNew("cms.new.auto.label.id_css", "ID CSS")}>
              <input
                type="text"
                value={style.attributes.cssId ?? ""}
                placeholder={cmsNew("cms.new.auto.placeholder.mon_ancre", "mon-ancre")}
                className={cn(CMS_PALETTE_TEXT_INPUT_CLASS, "h-9 w-40")}
                onChange={(e) =>
                  updateAttributes({ cssId: e.target.value.trim() || null })
                }
              />
            </ControlRow>
            <ControlRow label={cmsNew("cms.new.auto.label.classes_css", "Classes CSS")}>
              <input
                type="text"
                value={style.attributes.cssClass ?? ""}
                placeholder={cmsNew("cms.new.auto.placeholder.ma_classe_autre_classe", "ma-classe autre-classe")}
                className={cn(CMS_PALETTE_TEXT_INPUT_CLASS, "h-9 w-40")}
                onChange={(e) =>
                  updateAttributes({ cssClass: e.target.value.trim() || null })
                }
              />
            </ControlRow>
          </div>
        </StyleSection>
      ) : null}
    </div>
  );
}
